import { useEffect, useState } from "react";

import { Button } from "../../ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { PrcDialogContent } from "./PrcDialogContent";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import {
  dcbRateLabel,
  fmvBasisLabel,
  fmvSummaryLabel,
  useUpdateDiscountSpreadSetting,
  type DcbRate,
  type DiscountSpreadSetting,
  type FmvBasis,
} from "../../utils/portfolio-risk-concentration/api/discountSettings";

const DCB_RATES: DcbRate[] = ["OFF", "CONTRACT_IRR", "TREASURY_PLUS_SPREAD", "SOFR_PLUS_SPREAD", "FLAT"];
const FMV_BASES: FmvBasis[] = ["OFF", "TREASURY", "SOFR", "FLAT"];

/**
 * Adjust one raw investor code's DCB rate and FMV basis — direct writes, no propose/approve
 * (that table has none — see discount_settings.py's module docstring).
 *
 * Two entry points share this one dialog:
 *   Adjust      a row's own button — `setting` is pre-selected, the picker below never shows.
 *   Configure   the segment's top-right button — `setting` starts null and `settings` (every
 *               LIVE investor code) drives a picker; once one is chosen, the same fields
 *               below apply to it. Same write path either way.
 *
 * The FMV spread MATRIX itself (the grade x industry x term grid) is a separate, already-
 * governed editor (`ContractValuationSettings` — propose/approve, its own approval queue and
 * log) with its own top-right "Spread Matrix" button on the segment now — deliberately not
 * opened from inside this dialog anymore.
 *
 * ── Five DCB choices, three of which need an input ──────────────────────────────────────
 * OFF / CONTRACT_IRR need nothing. TREASURY_PLUS_SPREAD and SOFR_PLUS_SPREAD each show a
 * READ-ONLY current reference rate (never stored on this row — see discountSettings.ts) next
 * to a "Spread" box that IS stored, in that basis's own column, so the two never collide.
 * FLAT needs its own flat rate. Whichever field isn't showing keeps whatever value it last
 * had — switching away and back does not lose the number, matching the DB's own CHECK.
 *
 * ── The FMV grid has no facility dimension ──────────────────────────────────────────────
 * athena_input.prc_fair_value_spread prices ONCE for the whole book (see sql/28_/44_'s
 * headers) — so every code on TREASURY necessarily shows the same fmvRate/fmvVersion, and
 * likewise every code on SOFR.
 */
export function DiscountSpreadSettingsDialog({
  open,
  onOpenChange,
  settings,
  setting,
  onSubmitted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Every LIVE investor code, for the Configure entry point's picker. */
  settings: DiscountSpreadSetting[];
  /** Pre-selected (Adjust), or null to show the picker (Configure). */
  setting: DiscountSpreadSetting | null;
  onSubmitted?: (message: string) => void;
}) {
  const update = useUpdateDiscountSpreadSetting();
  const isConfigure = !setting;

  const [settingId, setSettingId] = useState("");
  const [dcbRate, setDcbRate] = useState<DcbRate>("CONTRACT_IRR");
  const [treasurySpreadPct, setTreasurySpreadPct] = useState("");
  const [sofrSpreadPct, setSofrSpreadPct] = useState("");
  const [dcbFlatPct, setDcbFlatPct] = useState("");
  const [fmvBasis, setFmvBasis] = useState<FmvBasis>("OFF");
  const [fmvFlatPct, setFmvFlatPct] = useState("");
  const [error, setError] = useState<string | null>(null);

  const active = setting ?? settings.find((s) => s.settingId === settingId) ?? null;

  function seedFrom(s: DiscountSpreadSetting | null) {
    setDcbRate(s?.dcbRate ?? "CONTRACT_IRR");
    setTreasurySpreadPct(s?.treasurySpreadPct !== null && s?.treasurySpreadPct !== undefined ? String(s.treasurySpreadPct) : "");
    setSofrSpreadPct(s?.sofrSpreadPct !== null && s?.sofrSpreadPct !== undefined ? String(s.sofrSpreadPct) : "");
    setDcbFlatPct(s?.dcbFlatPct !== null && s?.dcbFlatPct !== undefined ? String(s.dcbFlatPct) : "");
    setFmvBasis(s?.fmvBasis ?? "OFF");
    setFmvFlatPct(s?.fmvFlatPct !== null && s?.fmvFlatPct !== undefined ? String(s.fmvFlatPct) : "");
  }

  useEffect(() => {
    if (!open) return;
    setError(null);
    const seed = setting ?? settings[0] ?? null;
    setSettingId(seed?.settingId ?? "");
    seedFrom(seed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, setting]);

  function pickSetting(id: string) {
    setSettingId(id);
    seedFrom(settings.find((s) => s.settingId === id) ?? null);
  }

  /** A percent field's raw text -> a validated number, or an error message. */
  function parsePct(raw: string, fieldLabel: string): number | { error: string } {
    const n = Number(raw);
    if (raw.trim() === "" || !Number.isFinite(n)) {
      return { error: `Enter ${fieldLabel}, as a percent (8 for 8%).` };
    }
    if (n <= -100 || n >= 100) {
      return { error: `${fieldLabel} is a percent between -100 and 100.` };
    }
    return n;
  }

  async function submit() {
    if (!active) {
      setError("Choose an investor code.");
      return;
    }
    setError(null);

    const input: {
      settingId: string;
      dcbRate: DcbRate;
      treasurySpreadPct?: number | null;
      sofrSpreadPct?: number | null;
      dcbFlatPct?: number | null;
      fmvBasis: FmvBasis;
      fmvFlatPct?: number | null;
    } = { settingId: active.settingId, dcbRate, fmvBasis };

    if (dcbRate === "TREASURY_PLUS_SPREAD") {
      const parsed = parsePct(treasurySpreadPct, "a spread");
      if (typeof parsed !== "number") return setError(parsed.error);
      input.treasurySpreadPct = parsed;
    } else if (dcbRate === "SOFR_PLUS_SPREAD") {
      const parsed = parsePct(sofrSpreadPct, "a spread");
      if (typeof parsed !== "number") return setError(parsed.error);
      input.sofrSpreadPct = parsed;
    } else if (dcbRate === "FLAT") {
      const parsed = parsePct(dcbFlatPct, "a flat rate");
      if (typeof parsed !== "number") return setError(parsed.error);
      input.dcbFlatPct = parsed;
    }

    if (fmvBasis === "FLAT") {
      const parsed = parsePct(fmvFlatPct, "a flat rate");
      if (typeof parsed !== "number") return setError(parsed.error);
      input.fmvFlatPct = parsed;
    }

    try {
      await update.mutateAsync(input);
      onSubmitted?.(`${active.facilityLabel} discount & spread settings updated.`);
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PrcDialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isConfigure ? "Configure Discount & Spread Settings" : "Adjust Discount & Spread Settings"}
          </DialogTitle>
          <DialogDescription>
            {isConfigure
              ? "Pick an investor code, then set what rate Discounted Contract Balance discounts at, and Fair Value's basis."
              : `${setting!.facilityLabel} (code ${setting!.bankdwnInvestorCode}). What rate Discounted Contract Balance discounts at, and Fair Value's basis.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {error}
            </div>
          )}

          {isConfigure && (
            <div>
              <Label>Investor Code</Label>
              <Select value={settingId} onValueChange={pickSetting}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {settings.map((s) => (
                    <SelectItem key={s.settingId} value={s.settingId}>
                      {s.bankdwnInvestorCode} — {s.facilityLabel}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {active && (
            <>
              <div>
                <Label>DCB Discount Rate</Label>
                <Select value={dcbRate} onValueChange={(v) => setDcbRate(v as DcbRate)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DCB_RATES.map((r) => (
                      <SelectItem key={r} value={r}>
                        {dcbRateLabel(r)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {dcbRate === "TREASURY_PLUS_SPREAD" && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label>Current Treasury Rate</Label>
                    <div className="mt-1 flex h-9 items-center rounded-md border border-input bg-muted/40 px-3 text-sm text-muted-foreground">
                      {active.currentTreasuryPct !== null
                        ? `${active.currentTreasuryPct.toFixed(2)}%`
                        : "Not available"}
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="treasury-spread">Spread</Label>
                    <div className="relative mt-1">
                      <Input
                        id="treasury-spread"
                        inputMode="decimal"
                        className="pr-7"
                        placeholder="e.g. 1.50"
                        value={treasurySpreadPct}
                        onChange={(e) => setTreasurySpreadPct(e.target.value.replace(/[^0-9.-]/g, ""))}
                      />
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                        %
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {dcbRate === "SOFR_PLUS_SPREAD" && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label>Current SOFR Rate</Label>
                    <div className="mt-1 flex h-9 items-center rounded-md border border-input bg-muted/40 px-3 text-sm text-muted-foreground">
                      {active.currentSofrPct !== null ? `${active.currentSofrPct.toFixed(2)}%` : "Not available"}
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="sofr-spread">Spread</Label>
                    <div className="relative mt-1">
                      <Input
                        id="sofr-spread"
                        inputMode="decimal"
                        className="pr-7"
                        placeholder="e.g. 1.50"
                        value={sofrSpreadPct}
                        onChange={(e) => setSofrSpreadPct(e.target.value.replace(/[^0-9.-]/g, ""))}
                      />
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                        %
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {dcbRate === "FLAT" && (
                <div>
                  <Label htmlFor="dcb-flat">Flat Rate</Label>
                  <div className="relative mt-1">
                    <Input
                      id="dcb-flat"
                      inputMode="decimal"
                      className="pr-7"
                      placeholder="e.g. 6.25"
                      value={dcbFlatPct}
                      onChange={(e) => setDcbFlatPct(e.target.value.replace(/[^0-9.-]/g, ""))}
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                      %
                    </span>
                  </div>
                </div>
              )}

              <div className="rounded-md border border-border p-3">
                <div className="flex items-center justify-between gap-3">
                  <Label className="font-normal">FMV Spread Matrix</Label>
                  <span className="text-xs text-muted-foreground">{fmvSummaryLabel(active)}</span>
                </div>
                <div className="mt-3">
                  <Label>FMV Basis</Label>
                  <Select value={fmvBasis} onValueChange={(v) => setFmvBasis(v as FmvBasis)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FMV_BASES.map((b) => (
                        <SelectItem key={b} value={b}>
                          {fmvBasisLabel(b)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {fmvBasis === "FLAT" && (
                  <div className="mt-3">
                    <Label htmlFor="fmv-flat">Flat Rate</Label>
                    <div className="relative mt-1">
                      <Input
                        id="fmv-flat"
                        inputMode="decimal"
                        className="pr-7"
                        placeholder="e.g. 6.25"
                        value={fmvFlatPct}
                        onChange={(e) => setFmvFlatPct(e.target.value.replace(/[^0-9.-]/g, ""))}
                      />
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                        %
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={update.isPending} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={update.isPending || !active} onClick={submit}>
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </PrcDialogContent>
    </Dialog>
  );
}
